'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, MapPin, Users, Mail, Building2, GraduationCap, Lightbulb, Megaphone } from 'lucide-react'
import Image from 'next/image'

import { FadeUp } from '@/components/ui/fade-up'
export function DigitalPrideHiroshimaContent() {
  return (
    <div className="flex flex-col">
      {/* ヒーローセクション */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* 背景画像 */}
        <div className="absolute inset-0">
          <Image
            src="/images/news/digital_pride_hiroshima_open_2025.webp"
            alt="Digital Pride Hiroshima 拠点イメージ"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#BEB0A8]/70 via-[#BEB0A8]/60 to-transparent"></div>
        </div>

        <div className="container relative z-10 px-4 md:px-8 py-16">
          <div
            className="animate-fade-up max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-8 font-['Noto_Sans_JP',_sans-serif] bg-[#292623] text-white text-base px-4 py-2">
              2025年10月6日(月) オープン
            </Badge>

            <div className="mb-8">
              <Image
                src="/images/digital-pride/digital-pride-hiroshima.svg"
                alt="Digital Pride Hiroshima"
                width={800}
                height={200}
                className="mx-auto"
                priority
              />
            </div>

            <p className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[24px] text-[#292623] font-medium leading-relaxed">
              文化を成長力にするDX拠点、広島から始動
            </p>
          </div>
        </div>
      </section>

      {/* 開設の背景 */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623] mb-8 text-center">
              Digital Pride Hiroshima 開設の背景
            </h2>

            <div className="space-y-6 font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed">
              <p>
                デジタル技術の標準化が進む一方で、地域の文化や産業は独自性を発揮しにくくなっています。画一的なシステム導入が <strong className="text-[#292623]">「文化が失われるDX」</strong> を生みがちな現状に対し、株式会社CodeFoxは <strong className="text-[#292623]">「エンジニアリング＝文化翻訳」</strong> を掲げ、地域の文化やアイデンティティを成長力に変えるDXを実証してきました。
              </p>
              <p>
                <strong className="text-[#292623]">Digital Pride Hiroshima</strong> は、その思想を「見える・触れる・動かせる」形で社会に開くための拠点です。
              </p>

              <Card className="bg-[#BEB0A8]/20 border-[#BEB0A8]">
                <CardContent className="p-6">
                  <p className="text-sm">
                    <strong>拠点開発プロデュース：株式会社DoTS（DoTS Inc.）</strong><br/>
                    ※DoTSは「Design of Terminal SETOUCHI」を掲げ、瀬戸内の共創拠点「miobyDoTS」（広島駅ビルminamoa西棟3F）などを展開しています。
                  </p>
                </CardContent>
              </Card>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 拠点の役割 */}
      <section className="py-16 md:py-24 bg-[#BEB0A8]/30">
        <div className="container px-4">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623] mb-4">
              Digital Pride Hiroshima の役割
            </h2>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Lightbulb,
                title: "実証ラボ（開発・検証・展示）",
                description: "製造業・土木建設業・宿泊業・自治体など、多様な業種における文化翻訳型DXを実際に開発・検証。成果を来訪者が直接体験できる展示機能を併設。"
              },
              {
                icon: GraduationCap,
                title: "教育・人材育成",
                description: "DXを担う人材が不足している地域において、「文化翻訳を担う人材」を育成。学生・社会人向けに、AI駆動開発や地域DXの実務スキルを学べる研修・講座を提供。",
                note: "※すでに製造業を中心とした企業から教育出向を受け入れ、実業務のDXプロジェクトに参加しながら学べる仕組みを提供中。"
              },
              {
                icon: Users,
                title: "共創スペース",
                description: "地域企業、行政、金融機関、教育機関との協働プロジェクトを推進。共助型の地域DXを実現するためのワークショップやハッカソンを開催。"
              },
              {
                icon: Megaphone,
                title: "発信拠点",
                description: "成果事例を「広島モデル」として国内外へ発信。今後、島根・岡山など近隣地域への横展開を予定。"
              }
            ].map((role, index) => {
              const Icon = role.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full bg-white border-[#BEB0A8] hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 bg-[#BEB0A8]/20 rounded-lg">
                          <Icon className="h-6 w-6 text-[#292623]" />
                        </div>
                        <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[20px] font-bold text-[#292623] flex-1">
                          {role.title}
                        </h3>
                      </div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] md:text-[15px] text-[#292623]/70 leading-relaxed">
                        {role.description}
                      </p>
                      {role.note && (
                        <p className="mt-4 text-sm text-[#292623]/60 italic">
                          {role.note}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* リリースイベント */}
      <section id="event" className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623] mb-12 text-center">
              リリースイベント
            </h2>

            <Card className="border-[#BEB0A8]">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Calendar className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">日時</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] font-medium text-[#292623]">
                        2025年10月6日（月） 15:00〜
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">会場</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] font-medium text-[#292623] mb-2">
                        Digital Pride Hiroshima
                      </p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] text-[#292623]/70">
                        〒730-0053<br/>
                        広島県広島市中区東千田町1-1-61<br/>
                        hitoto広島ナレッジスクエア 1F
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Building2 className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">内容</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] text-[#292623]/70">
                        代表挨拶、来賓挨拶、施設見学、事例デモンストレーション、パネルディスカッション
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Users className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">来賓</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] text-[#292623]/70">
                        広島県内関係者（予定）
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeUp>
        </div>
      </section>

      {/* 代表メッセージ */}
      <section className="py-16 md:py-24 bg-[#BEB0A8]/20">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623] mb-12 text-center">
              代表メッセージ
            </h2>

            <div className="space-y-8">
              <Card className="bg-white border-[#BEB0A8]">
                <CardContent className="p-8">
                  <p className="font-['Noto_Sans_JP',_sans-serif] text-sm font-bold text-[#292623] mb-4">
                    株式会社CodeFox 代表取締役 進藤 史裕
                  </p>
                  <blockquote className="font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed border-l-4 border-[#BEB0A8] pl-6">
                    「DXは効率化のためではなく、文化を成長力に翻訳するためにあると私たちは考えています。<br/>
                    Digital Pride Hiroshimaは、その思想を体現する&ldquo;拠点&rdquo;です。広島の&ldquo;らしさ&rdquo;をデジタルに実装し、ここから新しいDXモデルを全国、そして世界へ広げていきます。」
                  </blockquote>
                </CardContent>
              </Card>

              <Card className="bg-white border-[#BEB0A8]">
                <CardContent className="p-8">
                  <p className="font-['Noto_Sans_JP',_sans-serif] text-sm font-bold text-[#292623] mb-4">
                    株式会社DoTS 代表取締役社長　谷口 千春
                  </p>
                  <blockquote className="font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed border-l-4 border-[#BEB0A8] pl-6">
                    「地域DX人材の〈共育推進拠点〉というDigital Pride Hiroshimaの思想・モデルに、DoTSとして大変共感しています。今後も豊富なネットワークとデザイン分野での知見を活かし、Digital Pride Hiroshimaのコミュニティ形成や事例形成に貢献して参ります。」
                  </blockquote>
                </CardContent>
              </Card>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 会社概要・問い合わせ */}
      <section id="contact" className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 会社概要 */}
            <FadeUp
            >
              <h2 className="font-['Shippori_Mincho_B1',_serif] text-[24px] md:text-[32px] font-bold text-[#292623] mb-8">
                会社概要
              </h2>

              <div className="grid md:grid-cols-2 gap-6 font-['Noto_Sans_JP',_sans-serif]">
                <div>
                  <p className="text-sm text-[#292623]/60 mb-1">会社名</p>
                  <p className="text-[15px] text-[#292623]">株式会社CodeFox</p>
                </div>
                <div>
                  <p className="text-sm text-[#292623]/60 mb-1">代表者</p>
                  <p className="text-[15px] text-[#292623]">代表取締役 進藤 史裕</p>
                </div>
                <div>
                  <p className="text-sm text-[#292623]/60 mb-1">設立</p>
                  <p className="text-[15px] text-[#292623]">2022年</p>
                </div>
                <div>
                  <p className="text-sm text-[#292623]/60 mb-1">事業内容</p>
                  <p className="text-[15px] text-[#292623]">DX支援事業、AI駆動型システム開発、教育事業、地域DX拠点運営</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-[#292623]/60 mb-1">所在地</p>
                  <p className="text-[15px] text-[#292623]">
                    〒730-0053 広島県広島市中区東千田町1-1-61 hitoto広島ナレッジスクエア 1F
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#292623]/60 mb-1">Web</p>
                  <a href="https://codefox.co.jp/" target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#8B6B47] hover:underline">
                    https://codefox.co.jp/
                  </a>
                </div>
              </div>
            </FadeUp>

            {/* お問い合わせ */}
            <FadeUp delay={0.2}
            >
              <h2 className="font-['Shippori_Mincho_B1',_serif] text-[24px] md:text-[32px] font-bold text-[#292623] mb-8">
                本件に関するお問い合わせ先
              </h2>

              <Card className="bg-[#BEB0A8]/10 border-[#BEB0A8]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Mail className="h-5 w-5 text-[#292623]" />
                    <p className="font-['Noto_Sans_JP',_sans-serif] text-[15px] font-medium text-[#292623]">
                      株式会社CodeFox 広報担当
                    </p>
                  </div>
                  <a
                    href="mailto:pr@codefox.co.jp"
                    className="font-['Noto_Sans_JP',_sans-serif] text-[15px] text-[#8B6B47] hover:underline"
                  >
                    pr@codefox.co.jp
                  </a>
                </CardContent>
              </Card>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  )
}
